import React, { useState, useEffect } from 'react';
import { QrCode, Copy, Check, Clock, Smartphone } from 'lucide-react';
import asaasService from '../../services/asaasService';
import { ResultadoPagamento } from '../../types/checkout';

interface PixPaymentProps {
  cobranca: any;
  onSuccess: (resultado: ResultadoPagamento) => void;
  onError: (error: string) => void;
}

const PixPayment: React.FC<PixPaymentProps> = ({ cobranca, onSuccess, onError }) => {
  const [qrCode, setQrCode] = useState<string>('');
  const [pixKey, setPixKey] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(15 * 60); // 15 minutos em segundos

  useEffect(() => {
    console.log('🔍 [PIX] Dados da cobrança recebidos:', cobranca);
    
    if (cobranca?.pixTransaction?.qrCode) {
      console.log('✅ [PIX] QR Code encontrado:', cobranca.pixTransaction.qrCode);
      setQrCode(cobranca.pixTransaction.qrCode);
      setPixKey(cobranca.pixTransaction.payload);
      setLoading(false);
    } else if (cobranca?.id) {
      console.log('⚠️ [PIX] QR Code não encontrado, mas ID existe:', cobranca.id);
      console.log('🔍 [PIX] Estrutura completa:', cobranca);
      // Manter loading = true para mostrar "Gerando código PIX..."
    } else {
      console.log('⚠️ [PIX] Estrutura de dados diferente:', {
        hasCobranca: !!cobranca,
        hasPixTransaction: !!cobranca?.pixTransaction,
        hasQrCode: !!cobranca?.pixTransaction?.qrCode,
        hasId: !!cobranca?.id,
        cobrancaKeys: cobranca ? Object.keys(cobranca) : []
      });
    }
  }, [cobranca]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          onError('Tempo limite para pagamento expirado. Tente novamente.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onError]);

  const copyPixKey = async () => {
    try {
      await navigator.clipboard.writeText(pixKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar chave PIX:', error);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const checkPaymentStatus = async () => {
    try {
      const status = await asaasService.consultarPagamento(cobranca.id);
      if (status.status === 'CONFIRMED') {
        onSuccess({
          success: true,
          paymentId: cobranca.id,
          message: 'Pagamento confirmado com sucesso!'
        });
      }
    } catch (error) {
      console.error('Erro ao verificar status do pagamento:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-md mx-auto p-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Gerando código PIX...</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-6">
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <Smartphone className="w-12 h-12 text-green-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Pagamento via PIX
        </h3>
        <p className="text-gray-600">
          Escaneie o QR Code ou copie a chave PIX
        </p>
      </div>

      {/* Timer */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-center mb-2">
          <Clock className="w-5 h-5 text-orange-500 mr-2" />
          <span className="text-sm font-medium text-orange-800">
            Tempo restante: {formatTime(timeLeft)}
          </span>
        </div>
        <p className="text-xs text-orange-600 text-center">
          O código PIX expira em {formatTime(timeLeft)}
        </p>
      </div>

      {/* QR Code */}
      {qrCode && (
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6 text-center">
          <div className="mb-4">
            <QrCode className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <h4 className="font-semibold text-gray-900">QR Code PIX</h4>
          </div>
          
          <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
            <img 
              src={`data:image/png;base64,${qrCode}`} 
              alt="QR Code PIX" 
              className="w-48 h-48 mx-auto"
            />
          </div>
          
          <p className="text-sm text-gray-600 mt-4">
            Escaneie com seu app de banco
          </p>
        </div>
      )}

      {/* Chave PIX */}
      {pixKey && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-gray-900 mb-3">Chave PIX</h4>
          <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3">
            <p className="text-sm text-gray-800 break-all">
              {pixKey}
            </p>
          </div>
          <button
            onClick={copyPixKey}
            className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Copiado!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Copiar Chave PIX
              </>
            )}
          </button>
        </div>
      )}

      {/* Instruções */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-blue-900 mb-2">Como pagar:</h4>
        <ol className="text-sm text-blue-800 space-y-1">
          <li>1. Abra o app do seu banco</li>
          <li>2. Escolha "PIX" ou "Pagar"</li>
          <li>3. Escaneie o QR Code ou cole a chave</li>
          <li>4. Confirme o pagamento</li>
        </ol>
      </div>

      {/* Verificar pagamento */}
      <button
        onClick={checkPaymentStatus}
        className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
      >
        Verificar Pagamento
      </button>

      {/* Informações adicionais */}
      <div className="mt-6 text-center">
        <p className="text-xs text-gray-500">
          💡 O pagamento é processado instantaneamente
        </p>
        <p className="text-xs text-gray-500">
          🔒 Transação 100% segura via Asaas
        </p>
      </div>
    </div>
  );
};

export default PixPayment;
